import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getSupabase } from "./supabase";
import { getAccessToken } from "./sessionToken";

interface AuthorizedFetchOptions extends RequestInit {
  allow404?: boolean;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// getAccessToken is provided by sessionToken.ts and always queries Supabase session

// Resolve API base URL at build-time (Vite inlines import.meta.env)
const API_BASE: string = ((import.meta as any).env?.VITE_API_BASE_URL as string) || "";

function withApiBase(url: RequestInfo | URL): RequestInfo | URL {
  try {
    if (typeof url === "string") {
      if (/^https?:\/\//i.test(url)) return url; // absolute
      if (url.startsWith("/")) {
        if (!API_BASE) return url; // no base configured → same-origin
        const base = API_BASE.replace(/\/$/, "");
        return base + url;
      }
      return url; // relative non-root → preserve as-is
    }
    return url;
  } catch {
    return url;
  }
}

export async function authorizedFetch(input: RequestInfo | URL, init: AuthorizedFetchOptions = {}): Promise<Response> {
  const { allow404 = false, headers: initHeaders, ...rest } = init;
  const token = await getAccessToken();
  const headers = new Headers(initHeaders ?? {});
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (rest.body instanceof FormData) {
    // Let browser set form-data boundary automatically
  } else if (!headers.has("Content-Type") && rest.body && typeof rest.body !== "string") {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(withApiBase(input), {
    ...rest,
    headers,
    credentials: rest.credentials ?? "include",
  });

  // Early error with readable context
  if (!res.ok) {
    if (allow404 && res.status === 404) {
      return res;
    }
    const text = await res.text().catch(() => "");
    const snippet = text ? text.slice(0, 180) : res.statusText;
    throw new Error(`HTTP ${res.status}: ${snippet}`);
  }

  // For non-JSON callers, returning Response is fine; they can .text()/.blob().
  // But we will guard common JSON misuse by detecting HTML content masquerading as JSON.
  const ct = String(res.headers.get("Content-Type") || "").toLowerCase();
  const looksJson = /application\/(json|.*\+json)/i.test(ct);
  if (!looksJson) {
    // Allow callers that explicitly want non-JSON to proceed: they can ignore this by not calling .json()
    // However, many flows immediately call .json(); detect obvious HTML fallbacks to provide a clearer error.
    const peek = await res.clone().text().catch(() => "");
    const isHtml = /^\s*<!doctype\s+html|<html[\s>]/i.test(peek);
    if (isHtml) {
      const snippet = peek.slice(0, 180);
      throw new Error(`Expected JSON, got ${ct || 'unknown'} :: ${snippet}`);
    }
  }

  return res;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isFormData = data instanceof FormData;
  const token = await getAccessToken();

  const headers: Record<string, string> = {};
  // Set Content-Type for requests with body, or for DELETE requests (server middleware requires it)
  if (!isFormData && (data || method === 'DELETE')) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(withApiBase(url), {
    method,
    headers,
    body: isFormData ? (data as FormData) : (data ? JSON.stringify(data) : undefined),
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const token = await getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Find the first segment starting with / to use as the request path
    const path = (() => {
      const parts = queryKey as Array<string | number>;
      const strParts = parts.map(String);
      const idx = strParts.findIndex((s) => s.startsWith('/'));
      return idx >= 0 ? strParts.slice(idx).join('/') : strParts.join('/');
    })();

    const res = await fetch(withApiBase(path as string) as string, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Helper to build keys safely with user/project scoping
export function scopedKey(parts: Array<string | number | null | undefined>): (string | number)[] {
  const safe = parts.filter((p) => p !== null && p !== undefined) as Array<string | number>;
  return safe;
}

// Back-compat: re-export getAccessToken for modules that import from queryClient
export { getAccessToken };
